import React from 'react';
import axios from 'axios';
import { baseUrl } from '../assets/Animations';
import { getRequestHeaders } from '../api';
import './dashboard-2024.css'
import { format, getDate, getMonth, isValid, sub } from 'date-fns';
import DashboardNotification from './DashboardNotification/DashboardNotification';
import { HorizontalLinkCard } from '../DanitComponents/Task Cards/HorizontalLinkCard';
import { getApplications, getCancellations, getInvoices, getOnboardingCards, getTasks, getTimesheets } from './dashboard2024Functions';

const Dashboard2024 = () => {
    const [students, setStudents] = React.useState([]);
    const [invoices, setInvoices] = React.useState([]);
    const [formattedConversionData, setFormattedConversionData] = React.useState([]);
    const [totals, setTotals] = React.useState({});
    const [onboardingCards, setOnboardingCards] = React.useState(0);
    const [cancellations, setCancellations] = React.useState(0);
    const [applications, setApplications] = React.useState(0);
    const [timesheets, setTimesheets] = React.useState(0);
    const [tasks, setTasks] = React.useState(0);
    const [unSubmittedInvoices, setUnSubmittedInvoices] = React.useState(0);
    const [month, setMonth] = React.useState('');
    const [year, setYear] = React.useState('');

    React.useEffect(() => {
        axios.get(`${baseUrl}/school-management/`, getRequestHeaders())
            .then(res => {
                setStudents(res.data);
            })
            .catch(err => {
                console.log(err);
            });

        axios.get(`${baseUrl}/allinvoices`, getRequestHeaders())
            .then(res => {
                setInvoices(res.data);
            })
            .catch(err => {
                console.log(err);
            });
            initializeDateSettings()
            async function fetchData() {
              await getTasks(setTasks);
              await getOnboardingCards(setOnboardingCards);
              await getCancellations(setCancellations);
            }
            fetchData();
    }, []);

    React.useEffect(() => {
      async function fetchData() {
        if (month && year) {
          await getTimesheets(month, year, setTimesheets);
          await getInvoices(month, year, setUnSubmittedInvoices);
          await getApplications(setApplications);
        }
      }
    
      fetchData();
    }, [month, year]);
    

    React.useEffect(() => {
        if(students.length > 0 && invoices.length > 0) {
            generateTotals(students)

      const organizedInquiries = organizeInquiriesByMonth(students);
      
      // Track how many families converted (were invoiced) by month
      const conversionResults = trackConvertedFamilies(organizedInquiries, invoices);
      const formattedConversionData = formatMonthlyConversionData(organizedInquiries, conversionResults);
        setFormattedConversionData(sortDataChronologically(formattedConversionData));
        }
    }, [students, invoices]);

    const sortDataChronologically = (data) => {
        return data.sort((a, b) => {
          return new Date(a.month) - new Date(b.month);
        });
      };
    const initializeDateSettings = () => {
        if(getDate(new Date()) < 15) {
            setMonth(getMonth(new Date()) !== 0 ? format(sub(new Date(), {months: 1}), 'MMMM') : 'December')
            setYear(getMonth(new Date()) !== 0 ? format(new Date(), 'yyyy') : format(sub(new Date(), {years: 1}), 'yyyy'))
        } else {
            setMonth(format(new Date(), 'MMMM'))
            setYear(format(new Date(), 'yyyy'))
        }
    }

    const organizeInquiriesByMonth = (inquiries) => {
        const inquiryMap = {}; // To store inquiries grouped by month/year
      
        // Helper function to format onboardDate into "MonthYear" (e.g., "June2024")
        const formatMonthYear = (date) => {
          const month = date.toLocaleString('default', { month: 'long' }); // Get month name
          const year = date.getFullYear(); // Get year
          //if the year is before 2021 return null
            if(year < 2021) {
                return null;
            }
          return `${month}${year}`; // Format like "June2024"
        };
      
        // Loop through the inquiries
        inquiries.forEach(inquiry => {
          const onboardDate = new Date(inquiry.onboardDate); // Parse onboardDate into Date object
          const monthYear = formatMonthYear(onboardDate); // Format it to "MonthYear"
      
          // Initialize an array for this monthYear if it doesn't exist
          if (!inquiryMap[monthYear]) {
            if(monthYear) {
                inquiryMap[monthYear] = [];
            }
          }
      
          // Add the inquiry to the corresponding month/year
          if(monthYear) {
            inquiryMap[monthYear].push(inquiry);
          }
        });
      
        return inquiryMap;
      };

      const trackConvertedFamilies = (organizedInquiries, invoices) => {
        const convertedFamilies = {}; // To store conversions for each inquiry month
      
        // Helper function to convert "June/2021" to a Date object (1st of the month)
        const parseMonthYear = (monthYearStr) => {
          const [month, year] = monthYearStr.split('/');
          const monthIndex = new Date(`${month} 1, ${year}`).getMonth(); // Month index (0-11)
          return new Date(year, monthIndex, 1); // Date object set to the 1st of the month
        };
      
        // Step 1: Create a set of all families that have been invoiced
        const invoicedFamilies = new Set();
        invoices.forEach(invoice => {
          invoicedFamilies.add(invoice.familyId);
        });
      
        // Step 2: Loop through the inquiries and check if the family has been invoiced
        for (const monthYear in organizedInquiries) {
          const inquiries = organizedInquiries[monthYear]; // Get inquiries for that month
          let convertedCount = 0; // Track how many inquiries converted in this month
      
          inquiries.forEach(inquiry => {
            const familyId = inquiry.familyId;
      
            // Check if this familyId exists in the invoicedFamilies set
            if (invoicedFamilies.has(familyId)) {
              convertedCount++; // Count as a conversion
            }
          });
      
          // Store the conversion count for this month
          convertedFamilies[monthYear] = convertedCount;
        }
      
        return convertedFamilies;
      };

      const tallyStudentsInquiries = (organizedInquiries) => {
        // Object to store the total number of students per month
        const studentTally = {};
      
        // Iterate over each monthYear in organizedInquiries
        Object.keys(organizedInquiries).forEach(monthYear => {
          let totalStudents = 0;
      
          // Iterate over each inquiry in the month and sum up the number of students
          organizedInquiries[monthYear].forEach(inquiry => {
            if (inquiry.students && Array.isArray(inquiry.students)) {
              totalStudents += inquiry.students.length; // Count the number of students in each inquiry
            }
          });
      
          // Store the result in the studentTally object
          studentTally[monthYear] = totalStudents;
        });
        //console.log(studentTally)
        return studentTally;
      };
      
      // Main function to format the monthly conversion data
      const formatMonthlyConversionData = (organizedInquiries, conversionResults) => {
        const formattedData = [];
        
        // Helper function to format conversion rate as a percentage
        const calculateConversionRate = (conversions, inquiries) => {
          if (inquiries === 0) return '0%'; // Avoid division by zero
          const rate = (conversions / inquiries) * 100;
          return `${Math.round(rate)}%`; // Return as rounded whole percentage
        };
      
        // Helper function to format monthYear from "Month2024" to "Aug 2023"
        const formatMonthYearDisplay = (monthYear) => {
          const date = new Date(monthYear);
        
          // Check if date is valid
          if (isNaN(date.getTime())) {
            console.error(`Invalid monthYear format: ${monthYear}`);
            return null; // Return original string if invalid
          }
        
          // Format the date to "MMM YYYY" (e.g., "Aug 2023")
          return date.toLocaleString('default', { month: 'short', year: 'numeric' });
        };
      
        // Get the student inquiries tally
        const studentTally = tallyStudentsInquiries(organizedInquiries);
      
        // Loop through the inquiry data to build the formatted result
        for (const monthYear in organizedInquiries) {
          const inquiries = organizedInquiries[monthYear].length; // Total inquiries for that month
          const studentInquiries = studentTally[monthYear] || 0; // Get student tally for that month
          const conversions = conversionResults[monthYear] || 0; // Total conversions for that month
          // Build the formatted object
          formattedData.push({
            month: formatMonthYearDisplay(monthYear), // Format the month and year
            inquiries: inquiries, // Total inquiries for that month
            numberOfStudentInquiries: studentInquiries, // Total number of students for that month
            conversion: conversions, // Total conversions for that month
            conversionRate: calculateConversionRate(conversions, inquiries) // Calculate and format the conversion rate
          });
        }
        //console.log(formattedData)
        return formattedData;
      };


      function projectInquiriesWeightedMovingAverage(historicalData, seasonalFactors) {
        let projectedInquiries = [];
        
        // Calculate weighted moving average for each month
        for (let i = 0; i < historicalData.length; i++) {
            // Use the last three months for a weighted average
            let weights = [0.5, 0.3, 0.2]; // Example weights for the last three months
            let weightedSum = 0;
            let totalWeight = 0;
    
            for (let j = 0; j < weights.length; j++) {
                if (i - j >= 0) { // Ensure we don't go out of bounds
                    weightedSum += historicalData[i - j] * weights[j];
                    totalWeight += weights[j];
                }
            }
    
            let averageInquiry = weightedSum / totalWeight;
            let nextInquiries = averageInquiry * seasonalFactors[i];
            projectedInquiries.push(Math.round(nextInquiries)); // Round to the nearest whole number
        }
    
        return projectedInquiries;
    }
    
    // Example usage:
    // Historical inquiries for the last 12 months
    let historicalData = [60, 65, 70, 75, 85, 90, 95, 100, 80, 70, 60, 55]; // Replace with your actual data
    let seasonalFactors = [1.1, 1.0, 0.9, 1.2, 1.3, 0.8, 0.9, 1.4, 1.5, 1.1, 0.95, 0.85]; // Example seasonal factors
    
    let projections = projectInquiriesWeightedMovingAverage(historicalData, seasonalFactors);
    //console.log(projections);
    
      

      const generateTotals = (students) => {
        //SET TOTALS BACK TO 0
        setTotals({ MMA: 0, LSMA: 0, TMA: 0, MOR: 0, PSMA:0 });
        //GENERATE TOTALS
        for (let i = 0; i < students.length; i++) {
          if (students[i].familyStatus === "Active") {
            for (let j = 0; j < students[i].students.length; j++) {
              students[i].students[j].studentStatus === "Active" &&
                setTotals((prev) => ({
                  ...prev,
                  [students[i].school]: prev[students[i].school] + 1,
                }));
            }
          }
        }
      };

      const redirectTo = (path) => { window.open(path, "_self").focus();}


    return (
        <div>
            <h1>Admin Dashboard</h1>
            <div className='dashboard-2024__whole-dashboard'>
                <div className='dashboard-2024__main-section'>
                  {totals['MMA'] ?
                <div className="dashboard-2024__total-section">
                      <div className="dashboard-2024__totals-box">
                        <div className='dashboard-2024__total-label'>TOTAL ACTIVE</div>
                        <div className='dashboard-2024__total__amount'>{totals["MMA"] + totals["LSMA"] + totals["TMA"] + totals["MOR"] + totals["PSMA"]}</div>
                      </div>
                      <div className="dashboard-2024__totals-box">
                          <div className='dashboard-2024__total-label'>MMA</div>
                          <div className='dashboard-2024__total__amount'>{totals["MMA"]}</div>
                      </div>
                      <div className="dashboard-2024__totals-box">
                          <div className='dashboard-2024__total-label'>LSMA</div>
                          <div className='dashboard-2024__total__amount'>{totals["LSMA"]}</div>
                      </div>
                      <div className="dashboard-2024__totals-box">
                          <div className='dashboard-2024__total-label'>TSMA</div>
                          <div className='dashboard-2024__total__amount'>{totals["TMA"]}</div>
                      </div>
                      <div className="dashboard-2024__totals-box">
                          <div className='dashboard-2024__total-label'>MOR</div>
                          <div className='dashboard-2024__total__amount'>{totals["MOR"]}</div>
                      </div>
                      <div className="dashboard-2024__totals-box">
                          <div className='dashboard-2024__total-label'>PSMA</div>
                          <div className='dashboard-2024__total__amount'>{totals["PSMA"]}</div>
                      </div>
                    </div>
                    :
                    <div className="dashboard-2024__total-section">
                    <div className="dashboard-2024__totals-box__with-flash">
                      <div className='dashboard-2024__total-label'>TOTAL ACTIVE</div>
                      <div className='dashboard-2024__total__amount'></div>
                    </div>
                    <div className="dashboard-2024__totals-box__with-flash">
                        <div className='dashboard-2024__total-label'>MMA</div>
                        <div className='dashboard-2024__total__amount'></div>
                    </div>
                    <div className="dashboard-2024__totals-box__with-flash">
                        <div className='dashboard-2024__total-label'>LSMA</div>
                        <div className='dashboard-2024__total__amount'></div>
                    </div>
                    <div className="dashboard-2024__totals-box__with-flash">
                        <div className='dashboard-2024__total-label'>TSMA</div>
                        <div className='dashboard-2024__total__amount'></div>
                    </div>
                    <div className="dashboard-2024__totals-box__with-flash">
                        <div className='dashboard-2024__total-label'>MOR</div>
                        <div className='dashboard-2024__total__amount'></div>
                    </div>
                    <div className="dashboard-2024__totals-box__with-flash">
                        <div className='dashboard-2024__total-label'>PSMA</div>
                        <div className='dashboard-2024__total__amount'></div>
                    </div>
                  </div>
                  }
            {
              formattedConversionData.length > 0 ? 
                <div className='dashboard-2024__inquiry-box'>
                <div className='dashboard-2024__inquiry-box__label'>INQUIRIES</div>
                  <div className="grid grid-cols-12 w-9/12 mx-auto my-5 cursor-pointer">
                    {formattedConversionData.map((backUpInfo, index) => (
                      backUpInfo.month &&
                      <div key={index} className="col-span-1 bg-white p-1 border-solid border-black border text-center">
                        <div className="conversion-bottom-box__month">{backUpInfo.month}</div>
                        <div className="text-lg font-bold">{backUpInfo.numberOfStudentInquiries}</div>
                        <div className='conversion-bottom-box'>
                            <div className="conversion-bottom-box__sub">{backUpInfo.conversion}</div>
                            <div className="conversion-bottom-box__sub">{Math.round(parseFloat(backUpInfo.conversionRate))}%</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
             : 
              <div className='dashboard-2024__inquiry-box__with-flash'>
                <div className='dashboard-2024__inquiry-box__label'>LOADING</div>
              </div>
            }
                          </div>
      <div className='dashboard-2024__right-column'>
        <div className='dashboard-2024__right-column__title'>NOTIFICATIONS</div>
        {onboardingCards ? <HorizontalLinkCard name={'Onboarding Cards'} action={() => redirectTo('/admin/students/adminonboarding3')} badge={{text: onboardingCards, color: {background: 'bg-blue-100', text: 'text-blue-500'}}} /> : null}
        {cancellations ? <HorizontalLinkCard name={'Cancellations'} action={() => redirectTo('/admin/teachers/cancellations')} badge={{text: cancellations, color: {background: 'bg-red-100', text: 'text-red-500'}}} /> : null}
        {applications ? <HorizontalLinkCard name={'Applications'} action={() => redirectTo('/admin/teachers/adminapplications')} badge={{text: applications, color: {background: 'bg-orange-100', text: 'text-orange-500'}}} /> : null}
        {timesheets ? <HorizontalLinkCard name={'Timesheets'} action={() => redirectTo('/admin/billing/timesheets')} badge={{text: timesheets, color: {background: 'bg-blue-100', text: 'text-blue-500'}}} /> : null}
        {unSubmittedInvoices ? <HorizontalLinkCard name={'Invoices'} action={() => redirectTo('/admin/billing/timesheets')} badge={{text: unSubmittedInvoices, color: {background: 'bg-blue-100', text: 'text-blue-500'}}} /> : null}
        {tasks ? <HorizontalLinkCard name={'Task Do Today'} action={() => redirectTo('/admin/tasks')} badge={{text: tasks, color: {background: 'bg-blue-100', text: 'text-blue-500'}}} /> : null}
      </div>
        </div>
        </div>
    );
}
export default Dashboard2024;